import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { createLink } from "@tanstack/react-router";

const spinnerSizeMap: Record<string, string> = {
  default: "h-4 w-4",
  sm: "h-4 w-4",
  lg: "h-5 w-5",
  icon: "h-4 w-4",
  "icon-sm": "h-4 w-4",
  unspaced: "h-4 w-4",
};

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg text-sm font-semibold transition-colors focus:outline-none focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-2px_0px_0px_rgba(0,0,0,0.05),inset_0px_0px_0px_1px_rgba(0,0,0,0.18)]",
        "default-light":
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 glass-small",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90",
        outline:
          "border border-neutral-300 bg-white text-neutral-700 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-2px_0px_0px_rgba(0,0,0,0.03)] hover:bg-neutral-50",
        secondary:
          "bg-secondary text-secondary-foreground glass-small hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        "ghost-soft":
          "text-neutral-700 hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        "error-link": "text-destructive underline-offset-4 hover:underline",
        "default-glass":
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 glass-small",
        dropdown:
          "min-w-54 justify-between border border-neutral-300 bg-white/60 shadow-sm hover:bg-white/80 hover:text-accent-foreground glass-small",
        selected:
          "border border-blue-200 bg-blue-50 text-blue-700 shadow-xs hover:bg-blue-50",
        "toggle-outline":
          "border border-neutral-300 bg-neutral-50 text-neutral-800 shadow-xs hover:border-blue-300 hover:bg-white",
      },
      size: {
        default: "h-8 px-3 py-1.5",
        sm: "h-8 px-3",
        lg: "h-10 px-8",
        icon: "h-8 w-8",
        "icon-sm": "h-8 w-8",
        unspaced: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

export const Button = ({
  ref,
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  disabled,
  children,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button";

  if (asChild) {
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  }

  const spinnerSize = spinnerSizeMap[size ?? "default"];

  return (
    <Comp
      className={cn(
        buttonVariants({ variant, size, className }),
        isLoading && "relative",
      )}
      ref={ref}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <span className="invisible inline-flex items-center gap-2">
            {children}
          </span>
          <span className="absolute inset-0 flex items-center justify-center">
            <Loader2 className={cn(spinnerSize, "animate-spin")} />
          </span>
        </>
      ) : (
        children
      )}
    </Comp>
  );
};
Button.displayName = "Button";

export const ButtonLink = createLink(Button);
